import { careWorkAreaCopy } from '@vivaporta/work-areas';
import { careIndustryPageKeys, type CareIndustryPageKey } from './careIndustryLabels.js';
import { retailIndustryPageKeys, retailIndustryLabels } from './retailIndustryLabels.js';
import type { LandingLocale } from './landingContentTypes.js';

export const newIndustryPageKeys = ['elektriker', 'bauwesen', 'schweisser', 'cnc', 'shk', 'lkw-fahrer'] as const;
export type NewIndustryPageKey = (typeof newIndustryPageKeys)[number];
export const industryPageKeys = ['pflege', 'einzelhandel', 'handwerk', 'gastro', ...newIndustryPageKeys, ...retailIndustryPageKeys, ...careIndustryPageKeys] as const;
export type IndustryPageKey = (typeof industryPageKeys)[number];
// Gastronomy remains available in existing editor projects but is intentionally retired publicly.
export const publicIndustryPageKeys = industryPageKeys.filter(key => key !== 'gastro');
export const isIndustryPageKey = (key: string): key is IndustryPageKey =>
  (industryPageKeys as readonly string[]).includes(key);

const labels: Record<LandingLocale, Record<IndustryPageKey, readonly [string, string]>> = {
  de: {
    ...Object.fromEntries(careIndustryPageKeys.map(key=>[key,[careWorkAreaCopy(key,'de').label,careWorkAreaCopy(key,'de').description] as const])) as Record<CareIndustryPageKey,readonly [string,string]>,
    ...retailIndustryLabels.de,
    pflege: ['Pflege', 'Pflege, Gesundheit und persönliche Begleitung'],
    einzelhandel: ['Einzelhandel', 'Verkauf, Frischetheke und Kundenkontakt'],
    handwerk: ['Handwerk', 'Praktisches Können mit Perspektive'],
    gastro: ['Gastronomie', 'Küche, Service und Gastfreundschaft'],
    elektriker: ['Elektriker / Elektroniker', 'Elektrotechnik, Installation und Elektronik'],
    bauwesen: ['Bauwesen', 'Tiefbau, Rohrbau und Trockenbau'],
    schweisser: ['Schweißer', 'Schweißen, Metallbau und Fertigung'],
    cnc: ['CNC / Zerspanung', 'Drehen, Fräsen und präzise Fertigung'],
    shk: ['Sanitär, Heizung & Klima', 'Gebäudetechnik, die den Alltag besser macht'],
    'lkw-fahrer': ['LKW-Fahrer', 'Transport, Logistik und Güterverkehr'],
  },
  en: {
    ...Object.fromEntries(careIndustryPageKeys.map(key=>[key,[careWorkAreaCopy(key,'en').label,careWorkAreaCopy(key,'en').description] as const])) as Record<CareIndustryPageKey,readonly [string,string]>,
    ...retailIndustryLabels.en,
    pflege: ['Care', 'Healthcare with personal guidance'],
    einzelhandel: ['Retail', 'Sales, fresh counters and customer contact'],
    handwerk: ['Skilled trades', 'Practical skills with a future'],
    gastro: ['Hospitality', 'Kitchen, service and hospitality'],
    elektriker: ['Electricians / electronics', 'Electrical work, installation and electronics'],
    bauwesen: ['Construction', 'Civil engineering, pipework and drywall'],
    schweisser: ['Welding', 'Welding, metalwork and manufacturing'],
    cnc: ['CNC / machining', 'Turning, milling and precision manufacturing'],
    shk: ['Plumbing, heating & cooling', 'Building services that improve everyday life'],
    'lkw-fahrer': ['Truck driving', 'Transport, logistics and freight'],
  },
  tr: {
    ...Object.fromEntries(careIndustryPageKeys.map(key=>[key,[careWorkAreaCopy(key,'tr').label,careWorkAreaCopy(key,'tr').description] as const])) as Record<CareIndustryPageKey,readonly [string,string]>,
    ...retailIndustryLabels.tr,
    pflege: ['Bakım', 'Sağlık, bakım ve kişisel destek'],
    einzelhandel: ['Perakende', 'Satış, taze ürün reyonları ve müşteri iletişimi'],
    handwerk: ['Zanaat', 'Becerilerine uygun mesleki bir gelecek'],
    gastro: ['Gastronomi', 'Mutfak, servis ve misafirperverlik'],
    elektriker: ['Elektrik / elektronik', 'Elektrik, montaj ve elektronik işleri'],
    bauwesen: ['İnşaat', 'Altyapı, boru döşeme ve alçıpan işleri'],
    schweisser: ['Kaynak', 'Kaynak, metal işleri ve üretim'],
    cnc: ['CNC / talaşlı imalat', 'Tornalama, frezeleme ve hassas üretim'],
    shk: ['Sıhhi tesisat, ısıtma & klima', 'Günlük yaşamı iyileştiren bina teknolojileri'],
    'lkw-fahrer': ['Kamyon şoförlüğü', 'Taşımacılık, lojistik ve yük taşımacılığı'],
  },
};

export const industryNavigationItems = (locale: LandingLocale) =>
  publicIndustryPageKeys.map(key => ({
    label: labels[locale][key][0],
    text: labels[locale][key][1],
    href: `/branchen/${key}`,
  }));
export const industryPageLabel = (key: IndustryPageKey, locale: LandingLocale) => labels[locale][key][0];
